import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styles from './SearchComponent.module.scss';
import { DebounceInput } from 'react-debounce-input';
import { CustomButton } from '../Button/Button';
import { Icon } from '../Icon';
import useOutsideClick from '../../hooks/useOutsideClick';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ESCAPE_KEY_CODE } from '../../constants/common';

const SearchComponent = ({
  searchValue,
  onChange,
  alwaysShow = false,
  clearButton = false, // string || boolean. string represents the clear button text
  className,
  inputClassName,
  maxLength = 500,
  searchState = () => { },
  onClose = () => { },
  onFocus,
  onBlur,
  placeholder = null,
  children,
}) => {
  const [isSearch, setIsSearch] = useState(alwaysShow);
  const inputRef = useRef(null);
  const fieldRef = useRef(null);
  const [showInput, setShowInput] = useState(alwaysShow);

  const { state } = useLocation();

  const { t } = useTranslation();

  const childrenWithProps = useMemo(
    () =>
      React.Children.map(children, child => {
        // Checking isValidElement is the safe way and avoids a
        // typescript error too.
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { inputRef });
        }
        return child;
      }),
    [inputRef, children]
  );

  const isCustomClearButton = useMemo(() => typeof clearButton === 'string' && clearButton.length > 0, [clearButton]);

  const handleToggleSearch = useCallback(() => {
    !alwaysShow && setIsSearch(prevState => !prevState);
  }, []);

  const handleDisableSearch = () => {
    setIsSearch(false);
    onClose();
  };

  useEffect(() => {
    !alwaysShow && setIsSearch(false);
  }, [state]);

  useEffect(() => {
    searchState(isSearch);
  }, [isSearch]);

  useEffect(() => {
    if (inputRef.current) {
      !alwaysShow && inputRef.current.focus();
    }
  }, [isSearch]);

  const handleChange = e => {
    onChange(e.target.value);
  };

  const handleClearInput = () => {
    onChange('');
  };

  useOutsideClick(fieldRef, () => {
    if (isSearch && searchValue.length === 0 && !alwaysShow) {
      handleDisableSearch();
    }
  });

  const handleKeyPress = useCallback(
    event => {
      if (event.keyCode === ESCAPE_KEY_CODE) {
        if (inputRef.current) {
          inputRef.current.blur();
        }

        !alwaysShow && handleDisableSearch();
        handleClearInput();
      }
    },
    [handleDisableSearch, handleClearInput, alwaysShow]
  );

  useEffect(() => {
    if (showInput && inputRef.current === document.activeElement) {
      document.addEventListener('keydown', handleKeyPress);
    } else {
      document.removeEventListener('keydown', handleKeyPress);
    }

    return () => showInput && document.removeEventListener('keydown', handleKeyPress);
  }, [showInput, document.activeElement]);

  useEffect(() => {
    if (!alwaysShow) {
      if (isSearch) {
        setTimeout(() => {
          setShowInput(isSearch);
        }, [250]);
      } else {
        setShowInput(isSearch);
      }
    }
  }, [isSearch]);

  return (
    <div className={cx(className, styles.searchWrapper, { [styles.active]: isSearch })}>
      <CustomButton className={cx(styles.button, styles.searchBtn)} variant="link" onClick={handleToggleSearch} ariaLabel="Search">
        <Icon src="search" className={styles.searchIcon} />
      </CustomButton>
      {showInput && (
        <div ref={fieldRef} className={styles.inputWrapper}>
          <DebounceInput
            debounceTimeout={300}
            className={cx(styles.searchInput, inputClassName)}
            inputRef={inputRef}
            type="text"
            name="Search Video"
            value={searchValue}
            onChange={handleChange}
            placeholder={placeholder ?? t('dashboard.searching.placeholder')}
            maxLength={maxLength}
            autoFocus={!alwaysShow}
            onFocus={onFocus}
            onBlur={onBlur}
            autoComplete="one-time-code"
            aria-label={t('accessibility.search_input')}
          />
          {(isCustomClearButton ? clearButton.length : clearButton) && !!searchValue.length && (
            <CustomButton className={cx(styles.button, styles.clearBtn)} variant="link" onClick={handleClearInput} ariaLabel="Clear" {...isCustomClearButton ? { label: clearButton } : {}}>
              {!isCustomClearButton && <Icon src="close" className={styles.clearIcon} />}
            </CustomButton>
          )}
          {childrenWithProps}
        </div>
      )}
    </div>
  );
};

export default SearchComponent;
